import { createTheme } from '@mui/material/styles';

const createModernPalette = (mode) => {
  const isDark = mode === 'dark';

  if (isDark) {
    return {
      mode: 'dark',
      primary: {
        main: '#1E90C3', // EdgeConneX Cyan
        light: '#6FBBD7',
        dark: '#1A7CA8',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#2A507B', // EdgeConneX Blue
        light: '#7C97B5',
        dark: '#1E3A5A',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#1A1B1F', // --bg-page
        paper: '#25272C', // --bg-surface
        elevated: '#2C2E34', // --bg-raised
      },
      surface: {
        main: '#25272C',
        light: '#2C2E34',
        dark: '#1A1B1F',
      },
      text: {
        primary: '#EDEEF0',
        secondary: '#AAAEB6',
        disabled: '#7E828B',
      },
      divider: '#33363D', // --border-subtle
      error: {
        main: '#F87171',
        light: '#FECACA',
        dark: '#DC2626',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#FBBF24',
        light: '#FDE68A',
        dark: '#D97706',
        contrastText: '#000000',
      },
      info: {
        main: '#3FA3C9', // focus / link
        light: '#6FBBD7',
        dark: '#1A7CA8',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#4ADE80',
        light: '#86EFAC',
        dark: '#059669',
        contrastText: '#FFFFFF',
      },
    };
  } else {
    return {
      mode: 'light',
      primary: {
        main: '#1E90C3', // EdgeConneX Cyan
        light: '#6FBBD7',
        dark: '#1A7CA8',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#2A507B', // EdgeConneX Blue
        light: '#7C97B5',
        dark: '#1E3A5A',
        contrastText: '#FFFFFF',
      },
      background: {
        default: '#F8FAFC',
        paper: '#FFFFFF',
        elevated: '#F1F5F9',
      },
      surface: {
        main: '#FFFFFF',
        light: '#F8FAFC',
        dark: '#E2E8F0',
      },
      text: {
        primary: '#0F172A',
        secondary: '#475569',
        disabled: '#94A3B8',
      },
      divider: 'rgba(0, 0, 0, 0.08)',
      error: {
        main: '#EF4444',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#F59E0B',
        contrastText: '#000000',
      },
      info: {
        main: '#3B82F6',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#10B981',
        contrastText: '#FFFFFF',
      },
    };
  }
};

const createModernTypography = () => ({
  fontFamily: [
    'Outfit',
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    'sans-serif',
  ].join(','),
  fontSize: 14,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { fontSize: '2.5rem', fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.02em' },
  h2: { fontSize: '2rem', fontWeight: 800, lineHeight: 1.3, letterSpacing: '-0.015em' },
  h3: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.4, letterSpacing: '-0.01em' },
  h4: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.4 },
  h5: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.5 },
  h6: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.5 },
  body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
  body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.57 },
  button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' },
  caption: { fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.6 },
});

const createModernComponents = (mode) => {
  const isDark = mode === 'dark';
  const palette = createModernPalette(mode);

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 18px',
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: isDark ? '0 4px 14px rgba(124, 58, 237, 0.3)' : '0 4px 14px rgba(109, 40, 217, 0.2)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: isDark
            ? 'linear-gradient(135deg, #1E90C3 0%, #2A507B 100%)'
            : 'linear-gradient(135deg, #1E90C3 0%, #2A507B 100%)',
          '&:hover': {
            background: isDark
              ? 'linear-gradient(135deg, #1A7CA8 0%, #1E3A5A 100%)'
              : 'linear-gradient(135deg, #1A7CA8 0%, #1E3A5A 100%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          backdropFilter: isDark ? 'blur(16px)' : 'none',
          borderRadius: '16px',
          border: `1px solid ${palette.divider}`,
        },
        elevation1: {
          boxShadow: isDark
            ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            : '0 2px 12px rgba(15, 23, 42, 0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          border: `1px solid ${palette.divider}`,
          backgroundColor: palette.background.paper,
          backdropFilter: isDark ? 'blur(16px)' : 'none',
          backgroundImage: 'none',
          boxShadow: isDark
            ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
            : '0 2px 12px rgba(15, 23, 42, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: isDark ? 'rgba(30, 144, 195, 0.5)' : 'rgba(30, 144, 195, 0.3)',
            boxShadow: isDark
              ? '0 12px 40px 0 rgba(30, 144, 195, 0.15), 0 0 20px 0 rgba(42, 80, 123, 0.15)'
              : '0 8px 24px rgba(30, 144, 195, 0.12)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.6)' : palette.background.paper,
            '& fieldset': {
              borderColor: palette.divider,
            },
            '&:hover fieldset': {
              borderColor: palette.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.primary.main,
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
          fontWeight: 600,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          borderBottomColor: palette.divider,
        },
        head: {
          fontWeight: 700,
          backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(248, 250, 252, 0.9)',
          backdropFilter: 'blur(8px)',
        },
      },
    },
  };
};

export const createAppTheme = (mode = 'light') => {
  const validMode = (mode === 'dark' || mode === 'light') ? mode : 'light';

  const palette = createModernPalette(validMode);
  const typography = createModernTypography();
  const components = createModernComponents(validMode);

  return createTheme({
    palette,
    typography,
    components,
    shape: {
      borderRadius: 12,
    },
    spacing: 8,
  });
};
